import {
  Hook,
  LOG_LEVEL_OPTIONS,
  RunsQuery as LogsQuery,
  STATUS_CODE_2XX_OPTIONS,
  STATUS_CODE_4XX_OPTIONS,
  STATUS_CODE_5XX_OPTIONS,
} from '@rossum/api-client/hooks';
import { SelectChangeEvent, Stack } from '@rossum/ui/material';
import SearchInput from '../../../../ui/search-input/SearchInput';
import { DatepickerFilters } from './DatepickerFilters';
import { FilterSelect } from './FilterSelect';
import { HooksAutocomplete } from './HooksAutocomplete';

const FILTERS_SELECT_WIDTH = 200;
const PAGE_SIZE_OPTIONS = ['5', '10', '25', '50', '100'];
const STATUS_CODES_OPTIONS = ['2xx', '4xx', '5xx'] as const;

type StatusKey = (typeof STATUS_CODES_OPTIONS)[number];
const statusMap = {
  '2xx': STATUS_CODE_2XX_OPTIONS,
  '4xx': STATUS_CODE_4XX_OPTIONS,
  '5xx': STATUS_CODE_5XX_OPTIONS,
};

type Props = {
  filters: LogsQuery;
  setFilters: (filters: LogsQuery) => void;
  extensions: Array<Hook> | undefined;
  isFetchingExtensions: boolean;
  isLoadingExtensions: boolean;
};

const Filters = ({
  isFetchingExtensions,
  isLoadingExtensions,
  extensions,
  filters,
  setFilters,
}: Props) => {
  const selectedCategories = Object.entries(statusMap)
    .filter(([_, v]) => v.every(code => filters.statusCode?.includes(code)))
    .map(([k]) => k);

  const handleChange = (
    event: SelectChangeEvent<number | string | string[]>,
    filterId: 'pageSize' | 'logLevel' | 'statusCode'
  ) => {
    if (filterId === 'statusCode') {
      const selected = event.target.value as StatusKey[];
      const selectedCodes = selected
        .flatMap(category => statusMap[category])
        .filter((value, index, self) => self.indexOf(value) === index);

      return setFilters({ ...filters, statusCode: selectedCodes });
    }

    const selected = event.target.value;
    return setFilters({ ...filters, [filterId]: selected });
  };

  const handleClear = (
    filterId: 'pageSize' | 'logLevel' | 'statusCode',
    isMultiple: boolean
  ) => {
    setFilters({
      ...filters,
      [filterId]: isMultiple ? [] : undefined,
    });
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      minHeight={40}
      alignItems="flex-start"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <DatepickerFilters
          filters={filters}
          setFilters={setFilters}
          width={FILTERS_SELECT_WIDTH}
        />
        <HooksAutocomplete
          extensions={extensions}
          value={filters.hook ?? []}
          setHookFilter={hooks =>
            setFilters({
              ...filters,
              hook: hooks,
            })
          }
          isFetchingExtensions={isFetchingExtensions}
          isLoadingExtensions={isLoadingExtensions}
          width={FILTERS_SELECT_WIDTH}
        />
        <FilterSelect
          filterId="logLevel"
          selectProps={{
            value: filters.logLevel,
            options: [...LOG_LEVEL_OPTIONS],
            multiple: false,
          }}
          onChange={handleChange}
          onClear={handleClear}
          isLoadingExtensions={isLoadingExtensions}
        />
        <FilterSelect
          filterId="statusCode"
          selectProps={{
            value: selectedCategories,
            options: [...STATUS_CODES_OPTIONS],
            multiple: true,
          }}
          onChange={handleChange}
          onClear={handleClear}
          isLoadingExtensions={isLoadingExtensions}
        />
        <FilterSelect
          filterId="pageSize"
          selectProps={{
            value: filters.pageSize,
            options: PAGE_SIZE_OPTIONS,
            multiple: false,
          }}
          onChange={handleChange}
          onClear={handleClear}
          isLoadingExtensions={isLoadingExtensions}
          width={120}
        />
      </Stack>
      <SearchInput
        onChange={(search: string) => {
          setFilters({
            ...filters,
            search: search.length > 0 ? search : undefined,
          });
        }}
        value={filters.search ?? ''}
      />
    </Stack>
  );
};

export { Filters };
